import { useState } from 'react';
import { Button, Paper, Stack, Typography } from '@mui/material';
import { Icon } from '@shared/icons';
import { useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { Product } from '@domain/product/models/Product';
import { useProducts } from '@features/products/hooks/useProducts';
import { useCompanySettings } from '@features/company/hooks/useCompanySettings';
import { useShipmentMethods } from '@features/shipments/hooks/useShipmentMethods';
import { useConnections } from '@features/connections/generic/hooks/useConnections';
import { useAmazonSync } from '@features/connections/amazon/hooks/useAmazon';

import { ClientType } from '@domain/connection/models/Connector';
import AddToCatalogDialog, { CatalogLeadTimes } from './AddToCatalogDialog';
import UnclassifiedProductRow from './UnclassifiedProductRow';

const UnclassifiedProductsBanner = () => {
  const { products, isLoading } = useProducts({ unclassified: true, page: 1, limit: 8, purchasable: true });
  const { settings } = useCompanySettings();
  const { shipmentMethods } = useShipmentMethods();
  const { connections } = useConnections();
  const { syncInventory } = useAmazonSync();
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rows = products?.status === 'success' ? products.data : [];
  const total = products?.status === 'success' ? products.total : 0;
  if (isLoading || total === 0) return null;

  const openFor = (ids: string[]) => {
    setError(null);
    setSelectedIds(ids);
  };

  const addAll = async () => {
    setError(null);
    try {
      const collected: Product[] = [];
      let page = 1;
      for (;;) {
        const result = await container.productRepository.getProducts({ unclassified: true, page, limit: 100, purchasable: true });
        if (result.status === 'error') throw new Error(result.error.message);
        collected.push(...result.data);
        if (!result.hasNext) break;
        page += 1;
      }
      openFor(collected.map(product => product.id));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Could not load products');
    }
  };

  const confirm = async (values: CatalogLeadTimes) => {
    if (!selectedIds?.length) return;
    setBusy(true);
    setError(null);
    try {
      for (const productId of selectedIds) {
        const updated = await container.productRepository.updateProduct(productId, {
          active: true,
          productionDuration: values.productionDuration,
          processingDuration: values.processingDuration,
          minimumDaysOfStockInReserve: values.bufferDays,
          minimumOrderAmountDays: values.orderAmountDays,
          defaultShipmentMethodId: values.shipmentMethodId,
        });
        if (updated.status === 'error') throw new Error(updated.error.message);
      }
      const amazonConnections = (connections ?? []).filter(connection => connection.connector?.type === ClientType.AMAZON);
      for (const connection of amazonConnections) {
        await syncInventory(connection.id);
      }
      await queryClient.invalidateQueries({ queryKey: ['products'] });
      setSelectedIds(null);
    } catch (confirmError) {
      setError(confirmError instanceof Error ? confirmError.message : 'Could not add the products');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <Typography variant="subtitle1">New products found. Add them to your catalog</Typography>
        <Button size="small" variant="contained" startIcon={<Icon icon="Plus01" />} onClick={addAll}>
          Add to catalog
        </Button>
      </Stack>
      {error && selectedIds == null && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{error}</Typography>}
      <Stack spacing={1} sx={{ mt: 2 }}>
        {rows.map(product => (
          <UnclassifiedProductRow key={product.id} product={product} onAdd={() => openFor([product.id])} />
        ))}
      </Stack>
      <AddToCatalogDialog
        open={selectedIds != null}
        busy={busy}
        error={error}
        settings={settings}
        shippingDays={shipmentMethods?.find(method => method.id === settings?.defaultShipmentMethodId)?.duration ?? 0}
        onClose={() => {
          if (!busy) setSelectedIds(null);
        }}
        onConfirm={confirm}
      />
    </Paper>
  );
};

export default UnclassifiedProductsBanner;
